// Razorpay client-side SDK helper
// Dynamically loads the Razorpay checkout script and opens the payment modal.

declare global {
  interface Window {
    Razorpay: new (options: RazorpayOptions) => RazorpayInstance;
  }
}

export interface RazorpayOptions {
  key: string;
  amount: number; // in paise
  currency: string;
  name: string;
  description: string;
  order_id: string;
  prefill: {
    name: string;
    email: string;
    contact: string;
  };
  theme: {
    color: string;
  };
  handler: (response: RazorpayPaymentResponse) => void;
  modal?: {
    ondismiss?: () => void;
  };
}

export interface RazorpayPaymentResponse {
  razorpay_order_id: string;
  razorpay_payment_id: string;
  razorpay_signature: string;
}

interface RazorpayInstance {
  open: () => void;
}

// ── Load Razorpay script ──────────────────────────────────────────────────────

let scriptLoaded = false;

export function loadRazorpayScript(): Promise<void> {
  return new Promise((resolve, reject) => {
    if (scriptLoaded || window.Razorpay) {
      scriptLoaded = true;
      resolve();
      return;
    }

    const script = document.createElement("script");
    script.src = "https://checkout.razorpay.com/v1/checkout.js";
    script.async = true;
    script.onload = () => {
      scriptLoaded = true;
      resolve();
    };
    script.onerror = () => reject(new Error("Failed to load Razorpay SDK"));
    document.body.appendChild(script);
  });
}

// ── Open Razorpay checkout modal ──────────────────────────────────────────────

export async function openRazorpayCheckout(
  options: RazorpayOptions,
): Promise<void> {
  await loadRazorpayScript();
  const rzp = new window.Razorpay(options);
  rzp.open();
}